// src/services/authService.js
import { jwtDecode } from 'jwt-decode';
import api from './api';

export async function login(email, mot_de_passe) {
    try {
        const response = await api.post('/users/login', { email, mot_de_passe });
        if (response.data.token) {
            localStorage.setItem('token', response.data.token); // Stocker le token dans le stockage local
        }
        return response.data;
    } catch (error) {
        if (error.response?.status === 429) {
            throw new Error('Trop de tentatives de connexion. Veuillez réessayer dans quelques minutes.');
        }
        // Le backend renvoie désormais un message générique (401) que l'email soit inconnu ou le mot de passe incorrect.
        throw new Error(error.response?.data?.error || 'Erreur lors de la connexion');
    }
}

export async function forgotPassword(email) {
    try {
        const response = await api.post('/users/forgot-password', { email });
        return response.data; // Retourne la réponse en cas de succès
    } catch (error) {
        throw new Error(error.response?.data?.error || 'Erreur lors de la demande de réinitialisation.');
    }
}

export async function resetPassword(id, token, nouveau_mot_de_passe) {
    try {
        const response = await api.post('/users/reset-password', { id, token, nouveau_mot_de_passe });
        return response.data; // Retourne la réponse en cas de succès
    } catch (error) {
        const errors = error.response?.data?.errors;
        throw new Error(error.response?.data?.error || errors?.[0]?.msg || 'Erreur lors de la réinitialisation du mot de passe.');
    }
}

export async function updateUser(userId, userData) {
    try {
        const response = await api.put(`/users/${userId}`, userData);
        return response.data; // Retourne les données mises à jour
    } catch (error) {
        throw new Error(error.response?.data?.error || 'Erreur lors de la mise à jour des informations utilisateur.');
    }
}

export async function getUserInfo(userId) {
    try {
        const response = await api.get(`/users/info/${userId}`);
        return response.data; // Retourne les données utilisateur
    } catch (error) {
        throw new Error(error.response?.data?.error || 'Erreur lors de la récupération des informations utilisateur.');
    }
}

export function logout() {
    localStorage.removeItem('token'); // Supprime le token pour déconnecter l'utilisateur
}

export function getToken() {
    return localStorage.getItem('token');
}

export function isAuthenticated() {
    return !!getToken();
}

export function getUserRoles() {
    const token = getToken();
    if (token) {
        const decoded = jwtDecode(token);
        return decoded.roles || [];
    }
    return [];
}

export function hasRole(roleId) {
    return getUserRoles().includes(roleId);
}

export function getUserIdFromToken() {
    const token = localStorage.getItem('token');
    if (token) {
        const decoded = jwtDecode(token);
        return decoded.id; // Assurez-vous que `id` correspond bien au champ dans votre JWT
    }
    throw new Error("Token invalide ou absent.");
}


export async function register(userData) {
    try {
        const response = await api.post('/users/register', userData);
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.error || 'Erreur lors de la création de compte');
    }
}

export async function getProviders() {
    try {
        const response = await api.get('/users/providersInfo');
        return response.data; // Retourne les prestataires
    } catch (error) {
        throw new Error(error.response?.data?.error || 'Erreur lors de la récupération des prestataires');
    }
}

export async function getProvidersForPublic() {
    try {
        const response = await api.get('/users/providers');
        return response.data; // Retourne les prestataires
    } catch (error) {
        throw new Error(error.response?.data?.error || 'Erreur lors de la récupération des prestataires');
    }
}

// Récupérer les photos d'un utilisateur
export async function getPortfolioPhotos(userId) {
    try {
        const response = await api.get(`/photos/user/${userId}`);
        return response.data.photos;
    } catch (error) {
        throw new Error(error.response?.data?.error || 'Erreur lors de la récupération des photos.');
    }
}

// Ajouter une photo au portefeuille
export async function uploadPortfolioPhoto(photoFile) {
    try {
        const formData = new FormData();
        formData.append('photo', photoFile);

        const response = await api.post('/photos/upload', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.error || 'Erreur lors du téléversement de la photo.');
    }
}

// Supprimer une photo
export async function deletePortfolioPhoto(photoId) {
    try {
        const response = await api.delete(`/photos/${photoId}`);
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.error || 'Erreur lors de la suppression de la photo.');
    }
}

// Téléverser un document de certification
export async function uploadDocument(documentFile) {
    try {
        const formData = new FormData();
        formData.append('document', documentFile);

        const response = await api.post('/upload/document', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });

        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.error || 'Erreur lors du téléversement du document.');
    }
}

// Récupérer son propre document de certification (fetch + blob, nécessite le header Authorization)
export async function getMyDocument() {
    try {
        const response = await api.get('/upload/document', {
            responseType: 'blob',
        });
        return response.data; // Blob du document
    } catch (error) {
        if (error.response?.status === 404) {
            return null; // Aucun document enregistré
        }
        throw new Error('Erreur lors de la récupération du document.');
    }
}

// Récupérer le document de certification d'un autre utilisateur (réservé aux comptes admin)
export async function getUserDocument(userId) {
    try {
        const response = await api.get(`/upload/document/${userId}`, {
            responseType: 'blob',
        });
        return response.data; // Blob du document
    } catch (error) {
        if (error.response?.status === 404) {
            return null; // Aucun document enregistré pour cet utilisateur
        }
        if (error.response?.status === 403) {
            throw new Error("Vous n'avez pas les droits pour consulter ce document.");
        }
        throw new Error('Erreur lors de la récupération du document.');
    }
}

/**
 * Téléverse une photo de profil pour l'utilisateur
 * @param {FormData} formData - Données du formulaire contenant l'image
 * @returns {Promise<Object>} - Réponse de l'API contenant les détails de l'image téléversée
 */
export async function uploadProfilePhoto(formData) {
    try {
        const response = await api.post('/upload/profile', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data; // Retourne la réponse contenant les détails de l'image
    } catch (error) {
        console.error('Erreur lors du téléversement de la photo de profil:', error);
        throw new Error(error.response?.data?.message || 'Erreur inconnue');
    }
}
